/* eslint-disable max-lines-per-function, complexity, max-len, no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars, jsx-a11y/label-has-associated-control, react/no-unescaped-entities, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { getProdSupabaseClient, supabase } from '@/lib/supabase/client';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle,
  XCircle,
  Search,
  Shield,
  Mail,
  Calendar,
  User as UserIcon,
  RefreshCcw,
  Trash2,
  FileText,
  Users,
  AlertCircle,
  Building2,
  Check,
  MoreVertical,
  Key,
  Ban
} from 'lucide-react';
import { UserAuditTab } from './UserAuditTab';
import { tenantAPI, Tenant } from '@/services/api/tenants';
import { useAllTenants } from '@/hooks/useTenants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { validateTenantSetup } from '@/lib/supabase/diagnostics';

interface UserApprovalRecord {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  auth_provider: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  approved_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  notes: string | null;
  tenant_id?: string | null;
  last_active_at?: string | null;
  // Auth user data
  email_confirmed_at?: string | null;
  last_sign_in_at?: string | null;
  invited_at?: string | null;
  user_role?: {
    role_id: string;
    role_name: string;
    role_display_name: string;
  } | null;
  user_tenants?: Tenant[];
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  system_role: string;
  priority: number;
}

export const UserApprovalsTab: React.FC = () => {
  const { user, session, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [approvals, setApprovals] = useState<UserApprovalRecord[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>(
    'all'
  );
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'users' | 'audit'>('users');
  const [selectedApproval, setSelectedApproval] = useState<UserApprovalRecord | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [actionNotes, setActionNotes] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<UserApprovalRecord | null>(null);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRoleId, setNewUserRoleId] = useState<string>('');
  const [newUserTenantId, setNewUserTenantId] = useState<string>('');
  const [newUserNotes, setNewUserNotes] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  // Get all available tenants for selection (admin mode - bypasses RLS)
  const {
    data: allTenants,
    isLoading: allTenantsLoading,
    error: allTenantsError
  } = useAllTenants();

  // Tenant setup validation state
  const [tenantSetupValidated, setTenantSetupValidated] = useState(false);
  const [_tenantSetupInfo, setTenantSetupInfo] = useState<{
    prodTenantsExists: boolean;
    prodTenantsCount: number;
    userTenantsExists: boolean;
    userTenantsCount: number;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Use regular prod client with RLS (super_admin users should have access via RLS policies)
  // Note: This page is only accessible to super_admin users, so RLS should allow access
  const getAdminProdClient = () => {
    return getProdSupabaseClient();
  };

  // For auth operations, we still need service role key to create users
  // This is a legitimate use case for service role key in browser (admin-only page)
  const getAdminAuthClient = () => {
    const supabaseUrl =
      import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
    const serviceRoleKey =
      import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
      import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

    if (!serviceRoleKey) {
      // Fallback to regular client - user creation might fail but other operations will work
      console.warn('Service role key not available. User creation operations may fail.');
      return supabase;
    }

    return createClient(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });
  };

  // Load approvals
  const loadApprovals = async () => {
    try {
      setLoading(true);

      // Use RPC function to get all user approvals (bypasses RLS for super admins)
      // This follows the SECURITY DEFINER pattern for admin operations
      // IMPORTANT: Use regular supabase client (not prod client) for RPC calls
      // PostgREST searches public schema by default for RPC calls
      let approvalsData: any[] | null = null;
      let approvalsError: any = null;

      const rpcResult = await supabase.rpc('get_all_user_approvals_for_admin');
      approvalsData = rpcResult.data;
      approvalsError = rpcResult.error;

      if (approvalsError) {
        // Fallback to direct query if RPC function doesn't exist yet
        console.warn(
          '[UserApprovalsTab] RPC function not available, falling back to direct query:',
          approvalsError
        );
        const adminClient = getAdminProdClient();
        const result = await adminClient
          .from('user_approvals')
          .select(
            'id, user_id, email, full_name, auth_provider, status, requested_at, approved_at, approved_by, approved_by_name, notes'
          )
          .order('requested_at', { ascending: false });

        if (result.error) {
          throw result.error;
        }
        // Use fallback data (but this will still respect RLS)
        approvalsData = result.data || [];
      }

      // Batch fetch all user auth data via RPC (avoids N+1 queries and doesn't need service role key)
      const userIds = (approvalsData || []).map(a => a.user_id);
      let authDataMap: Record<string, any> = {};

      if (userIds.length > 0) {
        const { data: authDataArray, error: authDataError } = await supabase.rpc(
          'get_users_auth_data_for_admin',
          { p_user_ids: userIds }
        );

        if (authDataError) {
          console.warn('[UserApprovalsTab] Error fetching batch auth data:', authDataError);
        } else if (authDataArray) {
          // Create a map for quick lookup
          authDataMap = (authDataArray as any[]).reduce(
            (acc, user) => {
              acc[user.user_id] = user;
              return acc;
            },
            {} as Record<string, any>
          );
        }
      }

      // For each approval, use role data from RPC and fetch tenants
      const approvalsWithRoles = await Promise.all(
        (approvalsData || []).map(async approval => {
          // Role data is already included in the RPC result
          let userRole = null;
          if (approval.role_id && approval.role_name) {
            userRole = {
              role_id: approval.role_id,
              role_name: approval.role_name,
              role_display_name: approval.role_display_name || approval.role_name
            };
          }
          let userTenantsList: Tenant[] = [];
          let emailConfirmedAt: string | null = null;
          let lastSignInAt: string | null = null;
          let invitedAt: string | null = null;
          let firstName: string | null = null;
          let lastName: string | null = null;

          // Use pre-fetched auth data from RPC
          const authUser = authDataMap[approval.user_id];
          if (authUser) {
            emailConfirmedAt = authUser.email_confirmed_at || null;
            lastSignInAt = authUser.last_sign_in_at || null;
            invitedAt = authUser.invited_at || null;
            firstName = authUser.first_name || null;
            lastName = authUser.last_name || null;

            // If not in metadata, try to parse from full_name
            if (!firstName && !lastName && authUser.full_name) {
              const nameParts = authUser.full_name.trim().split(/\s+/);
              firstName = nameParts[0] || null;
              lastName = nameParts.slice(1).join(' ') || null;
            }
          }

          if (approval.status === 'approved') {
            // Fetch tenants using admin client to bypass RLS
            try {
              userTenantsList = await tenantAPI.getUserTenants(approval.user_id, true); // Use admin client
              if (userTenantsList && userTenantsList.length > 0) {
                console.log(
                  `[UserApprovalsTab] Loaded ${userTenantsList.length} tenants for user ${approval.user_id}:`,
                  userTenantsList.map(t => t.name || t.tenant_name)
                );
              }
            } catch (error) {
              console.error(
                `[UserApprovalsTab] Error loading tenants for user ${approval.user_id}:`,
                error
              );
              // Silently handle tenant loading errors to prevent breaking the approval list
            }
          }

          return {
            ...approval,
            requested_at: approval.created_at || approval.requested_at || new Date().toISOString(), // RPC returns created_at
            first_name: firstName || approval.full_name?.split(' ')[0] || null,
            last_name: lastName || approval.full_name?.split(' ').slice(1).join(' ') || null,
            email_confirmed_at: emailConfirmedAt,
            last_sign_in_at: lastSignInAt,
            invited_at: invitedAt,
            user_role: userRole,
            user_tenants: userTenantsList
          };
        })
      );

      setApprovals(approvalsWithRoles);
    } catch (error) {
      toast({
        title: 'Error Loading Approvals',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to load user approvals. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load available roles from prod.roles
  const loadRoles = async () => {
    try {
      const adminClient = getAdminProdClient();
      const { data, error } = await adminClient
        .from('roles')
        .select('id, name, display_name, description, level, scope')
        .order('level', { ascending: false });

      if (error) throw error;

      // Map to expected interface (add system_role and priority from existing fields)
      const rolesWithMapping = (data || []).map(role => ({
        ...role,
        system_role: role.name, // Use name as system_role
        priority: role.level // Use level as priority
      }));

      setRoles(rolesWithMapping);
    } catch (error) {
      // Silently handle role loading errors
    }
  };

  // Validate tenant setup when component mounts or when tenant error occurs
  useEffect(() => {
    const checkTenantSetup = async () => {
      if (allTenantsError && !tenantSetupValidated) {
        try {
          const validation = await validateTenantSetup();
          setTenantSetupInfo(validation);
          setTenantSetupValidated(true);
        } catch (error) {
          // Silently handle validation errors
        }
      }
    };

    checkTenantSetup();
  }, [allTenantsError, tenantSetupValidated]);

  useEffect(() => {
    // Wait for a confirmed session before firing admin queries — a missing or
    // expired session gets permission-denied from the database (log noise)
    if (!session || authLoading) return;
    if (viewMode === 'users') {
      loadApprovals();
    }
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, session, authLoading]);

  // Filter approvals
  const filteredApprovals = approvals.filter(approval => {
    const matchesSearch =
      !searchTerm ||
      approval.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (approval.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;

    const matchesRole =
      roleFilter === 'all' ||
      (roleFilter === 'no_role' && !approval.user_role) ||
      approval.user_role?.role_id === roleFilter;

    const matchesTenant =
      tenantFilter === 'all' || approval.user_tenants?.some(tenant => tenant.id === tenantFilter);

    return matchesSearch && matchesStatus && matchesRole && matchesTenant;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredApprovals.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedApprovals = filteredApprovals.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, tenantFilter]);

  // Handle approve/reject
  const handleAction = async () => {
    if (!selectedApproval || !user) return;

    // Validate role selection for approval
    if (actionType === 'approve' && !selectedRoleId) {
      toast({
        title: 'Role Required',
        description: 'Please select a role for the user before approving.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Update approval status via RPC (bypasses RLS)
      const approvalStatus = actionType === 'approve' ? 'approved' : 'rejected';
      const { data: approvalResult, error: approvalError } = await supabase.rpc(
        'update_user_approval_for_admin',
        {
          p_user_id: selectedApproval.user_id,
          p_status: approvalStatus,
          p_approved_by: actionType === 'approve' ? user.id : null,
          p_approved_by_name:
            actionType === 'approve' ? user.email?.split('@')[0] || 'Admin' : null,
          p_notes: actionNotes || null
        }
      );

      if (approvalError) throw approvalError;

      if (approvalResult && !approvalResult['success']) {
        throw new Error(approvalResult['error'] || 'Failed to update approval');
      }

      // If approving, assign the selected role to the user via RPC
      if (actionType === 'approve' && selectedRoleId) {
        const tenantId = selectedApproval.tenant_id;

        if (!tenantId) {
          throw new Error(
            'User must have a tenant assigned in their approval record before approving'
          );
        }

        const { error: roleError } = await supabase.rpc('assign_user_role', {
          p_user_id: selectedApproval.user_id,
          p_tenant_id: tenantId,
          p_role_id: selectedRoleId,
          p_assigned_by: user.id
        });

        if (roleError) throw roleError;
      }

      toast({
        title: actionType === 'approve' ? 'User Approved' : 'User Rejected',
        description: `${selectedApproval.email} has been ${actionType === 'approve' ? 'approved' : 'rejected'}.`,
        variant: 'success'
      });

      // Note: Audit log for user_approval actions is automatically created by trigger
      // prod.trg_user_approvals_audit() which calls prod.write_audit_log()
      // So we don't need to manually insert for approve/reject actions
      // The trigger will handle: user_approval_approved, user_approval_rejected, etc.

      // Refresh list
      await loadApprovals();

      // Close dialog
      setActionDialogOpen(false);
      setSelectedApproval(null);
      setActionNotes('');
      setSelectedRoleId('');
    } catch (error) {
      toast({
        title: 'Action Failed',
        description: 'Failed to update user approval. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Handle role change for approved users
  const handleRoleChange = async (approval: UserApprovalRecord, newRoleId: string) => {
    if (!user) return;

    try {
      // Get tenant_id from the approval record (source of truth for JWT)
      const tenantId = approval.tenant_id;

      if (!tenantId) {
        toast({
          title: 'Error',
          description:
            'User must have a tenant assigned in their approval record before changing role.',
          variant: 'destructive'
        });
        return;
      }

      // Use assign_user_role RPC (SECURITY DEFINER, bypasses RLS)
      // This atomically: deactivates old roles for user+tenant, inserts new role
      const { error: roleError } = await supabase.rpc('assign_user_role', {
        p_user_id: approval.user_id,
        p_tenant_id: tenantId,
        p_role_id: newRoleId,
        p_assigned_by: user.id
      });

      if (roleError) throw roleError;

      toast({
        title: 'Role Updated',
        description: `Role changed successfully for ${approval.email}`,
        variant: 'success'
      });

      // Log audit event via RPC
      try {
        const newRole = roles.find(r => r.id === newRoleId);

        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: tenantId,
          p_action: 'user.role_changed',
          p_resource_type: 'user_role',
          p_resource_id: approval.user_id,
          p_old_values: {
            role_id: approval.user_role?.role_id,
            role_name: approval.user_role?.role_display_name,
            email: approval.email,
            full_name: approval.full_name
          },
          p_new_values: {
            role_id: newRoleId,
            role_name: newRole?.display_name,
            email: approval.email,
            full_name: approval.full_name,
            changed_from_ui: true
          }
        });
      } catch (auditError) {
        // Don't fail the operation if audit logging fails
      }

      // Refresh the list
      await loadApprovals();
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: 'Failed to update user role. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Open action dialog
  const openActionDialog = (approval: UserApprovalRecord, type: 'approve' | 'reject') => {
    setSelectedApproval(approval);
    setActionType(type);
    setActionDialogOpen(true);
  };

  // Open remove dialog
  const openRemoveDialog = (approval: UserApprovalRecord) => {
    setUserToRemove(approval);
    setRemoveDialogOpen(true);
  };

  // Handle tenant switch (single tenant only: replace current tenant with selected one)
  const handleTenantSwitch = async (approval: UserApprovalRecord, newTenantId: string) => {
    if (!user) return;

    const userId = approval.user_id;
    const currentTenantId = approval.tenant_id || approval.user_tenants?.[0]?.id;

    // No-op if already assigned to this tenant
    if (currentTenantId === newTenantId) return;

    const newTenant = allTenants?.find(t => t.id === newTenantId);
    const newTenantName = newTenant?.name || newTenant?.tenant_name || newTenantId;

    // Store original state for rollback
    const originalTenants = approval.user_tenants || [];
    const originalTenantId = approval.tenant_id;

    // Optimistic update: set user_tenants to [newTenant] and tenant_id to newTenantId
    const tenantForOptimistic = newTenant || {
      id: newTenantId,
      tenant_name: newTenantId,
      name: newTenantId
    };
    setApprovals(prev =>
      prev.map(a =>
        a.user_id !== userId
          ? a
          : { ...a, user_tenants: [tenantForOptimistic], tenant_id: newTenantId }
      )
    );

    toast({
      title: 'Tenant Switched',
      description: `${approval.email} is now assigned to ${newTenantName}.`,
      variant: 'success'
    });

    try {
      // Remove user from all current tenants
      for (const t of approval.user_tenants || []) {
        try {
          await tenantAPI.removeUserTenant(userId, t.id);
        } catch (removeErr) {
          console.warn(`[handleTenantSwitch] Error removing tenant ${t.id}:`, removeErr);
        }
      }

      // Add user to the new tenant
      await tenantAPI.addUserTenant(userId, newTenantId);

      // Always sync user_approvals.tenant_id and user_roles.tenant_id
      const { data, error: updateError } = await supabase.rpc('update_user_approval_for_admin', {
        p_user_id: userId,
        p_tenant_id: newTenantId
      });

      if (updateError) {
        console.error('[handleTenantSwitch] Error updating primary tenant:', updateError);
        throw updateError;
      }

      // The RPC returns HTTP 200 with {success: false, error} when its internal
      // logic fails (e.g. constraint collision rolled back). Surface that as a
      // thrown error so the catch block rolls back the optimistic update.
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to sync primary tenant');
      }

      // Audit log (non-blocking)
      supabase
        .rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: newTenantId,
          p_action: 'user.tenant_switched',
          p_resource_type: 'user_tenant',
          p_resource_id: userId,
          p_old_values: {
            email: approval.email,
            full_name: approval.full_name,
            previous_tenant_id: originalTenantId
          },
          p_new_values: {
            tenant_id: newTenantId,
            tenant_name: newTenantName,
            email: approval.email,
            full_name: approval.full_name,
            changed_from_ui: true
          }
        })
        .then(() => {})
        .catch(auditErr =>
          console.warn('[handleTenantSwitch] Audit log failed (non-fatal):', auditErr)
        );

      queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
      await loadApprovals();
    } catch (error) {
      console.error('[handleTenantSwitch] Error:', error);

      // Rollback optimistic update. Conditional spread for tenant_id satisfies
      // exactOptionalPropertyTypes when originalTenantId is undefined.
      const rollbackPatch = {
        user_tenants: originalTenants,
        ...(originalTenantId !== undefined && { tenant_id: originalTenantId })
      };
      setApprovals(prev => prev.map(a => (a.user_id !== userId ? a : { ...a, ...rollbackPatch })));

      toast({
        title: 'Update Failed',
        description:
          error instanceof Error ? error.message : 'Failed to switch tenant. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Helper function to create user records for existing users (single tenant only)
  const createUserRecordsForExistingUser = async (
    userId: string,
    email: string,
    firstName: string,
    lastName: string,
    roleId: string,
    tenantId: string,
    notes: string,
    approvedBy: string,
    adminProdClient: ReturnType<typeof getAdminProdClient>
  ) => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

    // Create/update approval record
    const { error: approvalError } = await adminProdClient.from('user_approvals').upsert(
      {
        user_id: userId,
        email: email,
        full_name: fullName,
        auth_provider: 'email',
        status: 'approved',
        requested_at: new Date().toISOString(),
        approved_at: new Date().toISOString(),
        approved_by: approvedBy,
        approved_by_name: user?.email?.split('@')[0] || 'Admin',
        notes: notes || 'Created by admin via Control Center.',
        tenant_id: tenantId
      },
      {
        onConflict: 'user_id'
      }
    );

    if (approvalError) throw approvalError;

    // Assign role
    const { error: roleError } = await adminProdClient.from('user_roles').upsert(
      {
        user_id: userId,
        tenant_id: tenantId,
        role_id: roleId,
        assigned_by: approvedBy,
        is_active: true
      },
      {
        onConflict: 'user_id,tenant_id,role_id'
      }
    );

    if (roleError) throw roleError;

    // Assign the single tenant
    try {
      await tenantAPI.addUserTenant(userId, tenantId);
    } catch (tenantError) {
      console.warn(`Failed to assign tenant ${tenantId} to user ${userId}:`, tenantError);
    }

    // Create profile record - use check-then-insert/update to avoid 400 errors with composite unique constraints
    try {
      const { data: existingProfile } = await adminProdClient
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (existingProfile) {
        // Update existing profile - only use columns that exist in prod.user_profiles
        // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
        await adminProdClient
          .from('user_profiles')
          .update({
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('tenant_id', tenantId);
      } else {
        // Insert new profile - only use columns that exist in prod.user_profiles
        // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
        const profileData = {
          user_id: userId,
          tenant_id: tenantId
          // Other fields (profile_photo_path, phone_number, job_title, department) are optional
        };

        console.log('[createUserRecords] Inserting user profile with data:', profileData);
        const { data: profileInsertData, error: profileError } = await adminProdClient
          .from('user_profiles')
          .insert(profileData)
          .select();

        if (profileError) {
          console.error('[createUserRecords] Failed to create user profile record:', {
            error: profileError,
            message: profileError.message,
            details: profileError.details,
            hint: profileError.hint,
            code: profileError.code,
            data: profileData
          });
        } else {
          console.log('[createUserRecords] Successfully created user profile:', profileInsertData);
        }
      }
    } catch (profileError) {
      console.warn('Failed to create user profile record:', profileError);
    }
  };

  // Handle create new user
  const handleCreateUser = async () => {
    if (!user) return;

    // Validate required fields
    if (
      !newUserEmail ||
      !newUserFirstName ||
      !newUserLastName ||
      !newUserRoleId ||
      !newUserTenantId
    ) {
      toast({
        title: 'Validation Error',
        description:
          'Please fill in all required fields including first name, last name, and a tenant.',
        variant: 'destructive'
      });
      return;
    }

    // Combine first and last name for full_name
    const newUserFullName = `${newUserFirstName.trim()} ${newUserLastName.trim()}`.trim();

    try {
      setCreatingUser(true);

      // Get admin clients for auth and database operations
      const adminAuthClient = getAdminAuthClient();
      const adminProdClient = getAdminProdClient();

      // Step 1: Invite user via Supabase - this sends "Invite user" email template
      // This is the PRIMARY method for new user setup following Supabase's invite flow:
      // - Creates user in "invited" state
      // - Sends email with one-time invite link
      // - User clicks link → lands on /auth/set-password → exchanges code for session → sets password
      // Use window.location.origin like the working commit c487680f2adac55adcb54cd344ec996393cfb0ea
      const redirectUrl = `${window.location.origin}/auth/set-password`;

      console.log('[handleCreateUser] Calling inviteUserByEmail with redirectUrl:', redirectUrl);
      const { data: inviteData, error: inviteError } =
        await adminAuthClient.auth.admin.inviteUserByEmail(newUserEmail, {
          data: {
            full_name: newUserFullName,
            first_name: newUserFirstName.trim(),
            last_name: newUserLastName.trim()
          },
          redirectTo: redirectUrl
        });

      console.log('[handleCreateUser] inviteUserByEmail response:', {
        hasData: !!inviteData,
        userId: inviteData?.user?.id,
        userEmail: inviteData?.user?.email,
        invitedAt: inviteData?.user?.invited_at,
        error: inviteError ? inviteError.message : null
      });

      if (inviteError) {
        console.log('[handleCreateUser] Invite error:', inviteError);
        console.log(
          '[handleCreateUser] Error code:',
          (inviteError as any)?.status || (inviteError as any)?.code
        );
        console.log('[handleCreateUser] Error message:', inviteError.message);

        // Check if user already exists - check both error code and message
        const errorCode = (inviteError as any)?.status || (inviteError as any)?.code;
        const errorMessage = inviteError.message?.toLowerCase() || '';
        const isExistingUser =
          errorCode === 'email_exists' ||
          errorCode === 422 ||
          errorMessage.includes('already registered') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('user with this email');

        // Check if this is a 500 error from trying to invite a previously deleted user
        // This happens when user was soft-deleted but still exists in auth.users
        const isEmailSendError =
          errorCode === 500 ||
          errorCode === 'unexpected_failure' ||
          errorMessage.includes('error sending invite email');

        if (isExistingUser || isEmailSendError) {
          console.log(
            '[handleCreateUser] User may already exist or was previously deleted, checking auth.users...'
          );
          // Check if user exists in auth.users
          const { data: usersData, error: listError } =
            await adminAuthClient.auth.admin.listUsers();
          if (listError) {
            console.error('[handleCreateUser] Error listing users:', listError);
            throw listError;
          }

          const existingUser = usersData?.users?.find(
            u => u.email?.toLowerCase() === newUserEmail.toLowerCase()
          );

          if (existingUser) {
            console.log('[handleCreateUser] Found existing user:', existingUser.id);

            // If the user was banned/deleted, we need to delete them completely and re-invite
            // Check if user is banned or in a bad state (no confirmation, etc.)
            const userIsBanned = existingUser.banned_until !== null;
            const userNeedsReactivation =
              isEmailSendError || userIsBanned || !existingUser.email_confirmed_at;

            if (userNeedsReactivation) {
              console.log(
                '[handleCreateUser] User needs reactivation, deleting and re-creating...'
              );

              // Delete the existing user from auth
              const { error: deleteError } = await adminAuthClient.auth.admin.deleteUser(
                existingUser.id
              );
              if (deleteError) {
                console.error('[handleCreateUser] Error deleting existing user:', deleteError);
                throw new Error(
                  `Failed to reset user account: ${deleteError.message}. Please try again or contact support.`
                );
              }

              console.log('[handleCreateUser] Deleted existing user, sending fresh invite...');

              // Now re-invite the user (recursively call the invite again)
              const { data: reInviteData, error: reInviteError } =
                await adminAuthClient.auth.admin.inviteUserByEmail(newUserEmail, {
                  data: {
                    full_name: newUserFullName,
                    first_name: newUserFirstName.trim(),
                    last_name: newUserLastName.trim()
                  },
                  redirectTo: redirectUrl
                });

              if (reInviteError) {
                console.error('[handleCreateUser] Re-invite error:', reInviteError);
                throw new Error(
                  `Failed to send invitation after account reset: ${reInviteError.message}`
                );
              }

              // Continue with the new user flow using the re-invited user
              if (reInviteData?.user?.id) {
                console.log(
                  '[handleCreateUser] User re-invited successfully:',
                  reInviteData.user.id
                );
                // Fall through to continue with normal user record creation below
                // by setting inviteData to the new result
                Object.assign(inviteData || {}, reInviteData);
              } else {
                throw new Error('User was re-invited but response did not include user ID');
              }
            } else {
              // User exists and is in good state, just update records
              const userId = existingUser.id;
              const primaryTenantId = newUserTenantId;

              // Create approval record and assign role for existing user
              await createUserRecordsForExistingUser(
                userId,
                newUserEmail,
                newUserFirstName,
                newUserLastName,
                newUserRoleId,
                primaryTenantId,
                newUserNotes,
                user.id,
                adminProdClient
              );

              toast({
                title: 'User Already Exists',
                description: `User ${newUserFullName} already exists. Records updated.`,
                variant: 'default'
              });

              // Reset form and close dialog
              setAddUserDialogOpen(false);
              setNewUserEmail('');
              setNewUserFirstName('');
              setNewUserLastName('');
              setNewUserRoleId('');
              setNewUserTenantId('');
              setNewUserNotes('');
              await loadApprovals();
              return;
            }
          } else if (!isEmailSendError) {
            // Only throw "user not found" error if this wasn't an email send error
            throw new Error('User already exists but could not be retrieved');
          } else {
            // Email send error but user doesn't exist - some other issue
            console.error('[handleCreateUser] Email send error but user not found in system');
            throw inviteError;
          }
        } else {
          // For other errors, throw them
          console.error(
            '[handleCreateUser] Invite failed with non-existing-user error:',
            inviteError
          );
          throw inviteError;
        }
      }

      // Get user ID from invite response or fetch it
      let userId: string;
      if (inviteData?.user?.id) {
        userId = inviteData.user.id;
      } else {
        // Fetch user by email to get ID (fallback if invite response doesn't include user)
        const { data: usersData, error: listError } = await adminAuthClient.auth.admin.listUsers();
        if (listError) throw listError;

        const createdUser = usersData?.users?.find(
          u => u.email?.toLowerCase() === newUserEmail.toLowerCase()
        );
        if (!createdUser) {
          throw new Error('User was created but could not be retrieved');
        }
        userId = createdUser.id;
      }
      const primaryTenantId = newUserTenantId;

      // Step 2: Create approval record with status 'approved' immediately (admin-driven)
      const approvalData = {
        user_id: userId,
        email: newUserEmail,
        full_name: newUserFullName,
        auth_provider: 'email',
        status: 'approved',
        requested_at: new Date().toISOString(),
        approved_at: new Date().toISOString(),
        approved_by: user.id,
        approved_by_name: user.email?.split('@')[0] || 'Admin',
        notes: newUserNotes || 'Created by admin via Control Center.',
        tenant_id: primaryTenantId
      };

      // Step 2: Create approval record via RPC function (bypasses RLS)
      console.log('[handleCreateUser] Creating approval record via RPC...');
      const { data: approvalId, error: approvalError } = await supabase.rpc(
        'create_user_approval',
        {
          p_user_id: userId,
          p_email: newUserEmail,
          p_full_name: newUserFullName,
          p_tenant_id: primaryTenantId,
          p_status: 'approved',
          p_auth_provider: 'email',
          p_approved_by: user.id,
          p_notes: null
        }
      );

      if (approvalError) {
        console.error('[handleCreateUser] Error creating approval record:', approvalError);
        throw new Error(
          `Failed to create approval record: ${approvalError.message} (Code: ${approvalError.code || 'unknown'})`
        );
      }
      console.log('[handleCreateUser] Created approval record:', approvalId);

      // Step 3: Assign role via RPC function (bypasses RLS)
      console.log('[handleCreateUser] Assigning role via RPC...');
      const { data: roleAssignmentId, error: roleError } = await supabase.rpc('assign_user_role', {
        p_user_id: userId,
        p_tenant_id: primaryTenantId,
        p_role_id: newUserRoleId,
        p_assigned_by: user.id
      });

      if (roleError) {
        console.error('[handleCreateUser] Error creating role assignment:', roleError);
        throw new Error(
          `Failed to assign role: ${roleError.message} (Code: ${roleError.code || 'unknown'})`
        );
      }
      console.log('[handleCreateUser] Created role assignment:', roleAssignmentId);

      // Step 4: Assign the single tenant via RPC function
      console.log(
        `[handleCreateUser] Assigning tenant ${primaryTenantId} to user ${userId} via RPC...`
      );
      const { data: userTenantId, error: tenantError } = await supabase.rpc('add_user_to_tenant', {
        p_user_id: userId,
        p_tenant_id: primaryTenantId
      });

      if (tenantError) {
        console.error('[handleCreateUser] Error assigning tenant:', tenantError);
        throw new Error(
          `Failed to assign tenant: ${tenantError.message} (Code: ${tenantError.code || 'unknown'})`
        );
      }
      console.log('[handleCreateUser] Successfully assigned tenant:', userTenantId);

      // Step 5: Create basic user profile record in prod.user_profiles for profile sync
      // Use check-then-insert/update to avoid 400 errors with composite unique constraints

      try {
        const { data: existingProfile } = await adminProdClient
          .from('user_profiles')
          .select('id')
          .eq('user_id', userId)
          .eq('tenant_id', primaryTenantId)
          .maybeSingle();

        if (existingProfile) {
          // Update existing profile - only use columns that exist in prod.user_profiles
          // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
          const { error: profileError } = await adminProdClient
            .from('user_profiles')
            .update({
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('tenant_id', primaryTenantId);

          if (profileError) {
            console.warn('Failed to update user profile record:', profileError);
          }
        } else {
          // Insert new profile - only use columns that exist in prod.user_profiles
          // Actual columns: id, user_id, tenant_id, profile_photo_path, phone_number, job_title, department, created_at, updated_at
          const profileData = {
            user_id: userId,
            tenant_id: primaryTenantId
            // Other fields (profile_photo_path, phone_number, job_title, department) are optional
          };

          console.log('[handleCreateUser] Inserting user profile with data:', profileData);
          const { data: profileInsertData, error: profileError } = await adminProdClient
            .from('user_profiles')
            .insert(profileData)
            .select();

          if (profileError) {
            console.error('[handleCreateUser] Failed to create user profile record:', {
              error: profileError,
              message: profileError.message,
              details: profileError.details,
              hint: profileError.hint,
              code: profileError.code,
              data: profileData
            });
            // Don't throw - profile creation is non-blocking
          } else {
            console.log('[handleCreateUser] Successfully created user profile:', profileInsertData);
          }
        }
      } catch (profileError) {
        // Non-blocking - profile creation is optional at this stage
        console.warn('Error creating user profile:', profileError);
      }

      // Step 6: Log audit event via RPC (non-blocking, fire and forget)
      try {
        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: primaryTenantId,
          p_action: 'user.created',
          p_resource_type: 'user',
          p_resource_id: userId,
          p_old_values: null,
          p_new_values: {
            email: newUserEmail,
            first_name: newUserFirstName.trim(),
            last_name: newUserLastName.trim(),
            full_name: newUserFullName,
            auth_provider: 'email',
            role_id: newUserRoleId,
            role_name: roles.find(r => r.id === newUserRoleId)?.display_name,
            tenant_id: newUserTenantId,
            created_via: 'control_center',
            created_by: user.email,
            notes: newUserNotes
          }
        });
      } catch (auditError) {
        // Silently handle audit logging errors - don't fail the operation
        console.warn('Failed to log audit event for user creation:', auditError);
      }

      // Instant feedback pattern: Close dialog and show success immediately
      setAddUserDialogOpen(false);
      toast({
        title: 'User Invited Successfully',
        description: `An invitation email has been sent to ${newUserEmail}. They will receive a one-time invite link to set their password.`,
        variant: 'success'
      });

      // Reset form
      setNewUserEmail('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserRoleId('');
      setNewUserTenantId('');
      setNewUserNotes('');

      // Refresh approvals list in background
      loadApprovals().catch(err => {
        console.warn('Failed to refresh approvals list:', err);
      });
    } catch (error) {
      console.error('[handleCreateUser] Error creating user:', error);

      // Ensure dialog is closed even on error
      setAddUserDialogOpen(false);

      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null
            ? JSON.stringify(error)
            : 'An unexpected error occurred. Please try again.';

      // Log full error details for debugging
      if (error && typeof error === 'object' && 'code' in error) {
        console.error('[handleCreateUser] Error code:', error.code);
      }
      if (error && typeof error === 'object' && 'details' in error) {
        console.error('[handleCreateUser] Error details:', error.details);
      }
      if (error && typeof error === 'object' && 'hint' in error) {
        console.error('[handleCreateUser] Error hint:', error.hint);
      }

      toast({
        title: 'Failed to Create User',
        description: errorMessage,
        variant: 'destructive'
      });

      // Reset form on error
      setNewUserEmail('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserRoleId('');
      setNewUserTenantId('');
      setNewUserNotes('');
    } finally {
      setCreatingUser(false);
    }
  };

  // Handle user removal - completely delete user from system
  const handleRemoveUser = async () => {
    if (!userToRemove || !user) return;

    try {
      const adminAuthClient = getAdminAuthClient();
      const adminProdClient = getAdminProdClient();

      // Log audit event BEFORE deletion (so we have the data) via RPC
      const tenantId = userToRemove.user_tenants?.[0]?.id || null;
      try {
        await supabase.rpc('create_audit_log', {
          p_user_id: user.id,
          p_tenant_id: tenantId,
          p_action: 'user.deleted',
          p_resource_type: 'user',
          p_resource_id: userToRemove.user_id,
          p_old_values: {
            status: userToRemove.status,
            role: userToRemove.user_role,
            email: userToRemove.email,
            full_name: userToRemove.full_name,
            auth_provider: userToRemove.auth_provider,
            tenant_ids: userToRemove.user_tenants?.map(t => t.id) || []
          },
          p_new_values: {
            deleted: true,
            deleted_at: new Date().toISOString(),
            deleted_by: user.id,
            deleted_from: 'control_center_user_approvals'
          }
        });
      } catch (auditError) {
        // Log but don't fail - continue with deletion
        console.warn('Failed to log audit event for user deletion:', auditError);
      }

      // Instant feedback: Close dialog immediately and show progress toast
      setRemoveDialogOpen(false);
      toast({
        title: 'Deleting User',
        description: `${userToRemove.email} is being removed from the system...`,
        variant: 'default'
      });

      // Use delete_user_everywhere RPC function via public wrapper - handles all cleanup in one call
      console.log(
        `[handleRemoveUser] Calling delete_user_everywhere for user: ${userToRemove.user_id}`
      );
      const { data: deleteResult, error: deleteError } = await supabase.rpc(
        'delete_user_everywhere',
        {
          p_auth_user_id: userToRemove.user_id,
          p_dry_run: false
        }
      );

      if (deleteError) {
        console.error('[handleRemoveUser] delete_user_everywhere error:', deleteError);
        throw deleteError;
      }

      console.log('[handleRemoveUser] User deletion succeeded:', deleteResult);

      toast({
        title: 'User Deleted',
        description: `${userToRemove.email} has been permanently removed from the system.`,
        variant: 'success'
      });

      // Refresh list to show current state
      loadApprovals().catch(err => console.warn('Failed to refresh approvals:', err));

      setUserToRemove(null);
    } catch (error) {
      console.error('Error during user deletion process:', error);

      // Dialog is already closed from instant feedback pattern
      // Show error toast
      toast({
        title: 'Deletion Error',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred during user deletion. Some records may have been removed. Please refresh to check the current state.',
        variant: 'destructive'
      });

      // Refresh list to show current state
      loadApprovals().catch(err => console.warn('Failed to refresh approvals:', err));

      setUserToRemove(null);
    }
  };

  // Get user status badge (invited, password set, active)
  const getUserStatus = (approval: UserApprovalRecord) => {
    // If user has logged in, they are active
    if (approval.last_sign_in_at) {
      return {
        status: 'active',
        badge: (
          <Badge className='bg-green-500/20 text-green-400 border-green-500/30 dark:bg-green-500/30 dark:text-green-300 dark:border-green-400/30'>
            <CheckCircle className='h-3 w-3 mr-1' />
            Active
          </Badge>
        )
      };
    }

    // If email is confirmed (password set), but never logged in
    if (approval.email_confirmed_at) {
      return {
        status: 'password_set',
        badge: (
          <Badge className='bg-blue-500/20 text-blue-400 border-blue-500/30 dark:bg-blue-500/30 dark:text-blue-300 dark:border-blue-400/30'>
            <Key className='h-3 w-3 mr-1' />
            Password Set
          </Badge>
        )
      };
    }

    // If invited but no password set
    if (approval.invited_at || approval.status === 'approved') {
      return {
        status: 'invited',
        badge: (
          <Badge className='bg-yellow-500/20 text-yellow-400 border-yellow-500/30 dark:bg-yellow-500/30 dark:text-yellow-300 dark:border-yellow-400/30'>
            <Mail className='h-3 w-3 mr-1' />
            Invited
          </Badge>
        )
      };
    }

    // Default fallback
    return {
      status: 'unknown',
      badge: <Badge variant='outline'>Unknown</Badge>
    };
  };

  // Get email status badge (tracks invite email lifecycle)
  const getEmailStatusBadge = (approval: UserApprovalRecord) => {
    // User has set password (email confirmed)
    if (approval.email_confirmed_at) {
      return (
        <Badge className='bg-green-500/20 text-green-600 border-green-500/30 dark:bg-green-500/30 dark:text-green-300 dark:border-green-400/30'>
          <CheckCircle className='h-3 w-3 mr-1' />
          Password Set
        </Badge>
      );
    }

    // Invite email was sent but not yet acted upon
    if (approval.invited_at || approval.status === 'approved') {
      return (
        <Badge className='bg-amber-500/20 text-amber-600 border-amber-500/30 dark:bg-amber-500/30 dark:text-amber-300 dark:border-amber-400/30'>
          <Mail className='h-3 w-3 mr-1' />
          Email Sent
        </Badge>
      );
    }

    // No invite sent yet (pending approval or just created)
    return (
      <Badge variant='outline' className='text-muted-foreground'>
        <AlertCircle className='h-3 w-3 mr-1' />
        Not Sent
      </Badge>
    );
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get redirect URL based on current origin - works for both dev and production
  const getRedirectUrl = (path: string) => {
    // Always use window.location.origin to ensure correct domain
    // This works for localhost, portal.vehix.ai, or any other deployment
    return `${window.location.origin}${path}`;
  };

  // Send confirmation link (resend invitation)
  // Uses OTP-based flow - email contains 6-digit code, user goes to /invite/confirm
  const handleSendConfirmationLink = async (approval: UserApprovalRecord) => {
    if (!user) return;

    try {
      const adminAuthClient = getAdminAuthClient();

      // Use OTP flow URL - users enter code manually at /invite/confirm
      // This is scanner-safe (email security systems can't consume OTP codes)
      const redirectUrl = `${window.location.origin}/invite/confirm`;

      // Resend invitation email (uses "Invite User" template with {{ .Token }} OTP code)
      const { error: inviteError } = await adminAuthClient.auth.admin.inviteUserByEmail(
        approval.email,
        {
          data: {
            full_name:
              approval.full_name ||
              `${approval.first_name || ''} ${approval.last_name || ''}`.trim(),
            first_name: approval.first_name || '',
            last_name: approval.last_name || ''
          },
          redirectTo: redirectUrl
        }
      );

      if (inviteError) {
        throw inviteError;
      }

      toast({
        title: 'Confirmation Link Sent',
        description: `A confirmation email has been sent to ${approval.email}`,
        variant: 'success'
      });

      // Refresh to update invited_at timestamp
      await loadApprovals();
    } catch (error) {
      console.error('Error sending confirmation link:', error);
      toast({
        title: 'Error Sending Link',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to send confirmation link. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Send reset password link
  const handleSendResetPasswordLink = async (approval: UserApprovalRecord) => {
    if (!user) return;

    try {
      const adminAuthClient = getAdminAuthClient();

      // Get environment-appropriate redirect URL
      const redirectUrl = getRedirectUrl('/auth/reset-password/confirm');

      // Send reset password email (OTP flow: template uses {{ .Token }}, user enters code at /auth/reset-password/confirm)
      // Uses "Reset password" template; ensure template uses {{ .Token }} and {{ .SiteURL }}/auth/reset-password/confirm
      const { error: resetError } = await adminAuthClient.auth.resetPasswordForEmail(
        approval.email,
        {
          redirectTo: redirectUrl
        }
      );

      if (resetError) {
        throw resetError;
      }

      toast({
        title: 'Reset Password Link Sent',
        description: `A password reset email has been sent to ${approval.email} using the Reset password template.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Error sending reset password link:', error);
      toast({
        title: 'Error Sending Reset Link',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to send reset password link. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Revoke user access (reject approval)
  const handleRevokeAccess = async (approval: UserApprovalRecord) => {
    if (!user || !approval) return;

    // Use existing reject logic
    setSelectedApproval(approval);
    setActionType('reject');
    setActionNotes('');
    setActionDialogOpen(true);
  };

  if (loading && viewMode === 'users') {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
        <span className='ml-2'>Loading approvals...</span>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold tracking-tight'>User Setup</h2>
          <p className='text-muted-foreground'>Manage user access requests and role assignments</p>
        </div>
        <div className='flex items-center bg-muted p-1 rounded-lg'>
          <Button
            variant={viewMode === 'users' ? 'secondary' : 'ghost'}
            size='sm'
            className='gap-2'
            onClick={() => setViewMode('users')}
          >
            <Users className='h-4 w-4' />
            Users
          </Button>
          <Button
            variant={viewMode === 'audit' ? 'secondary' : 'ghost'}
            size='sm'
            className='gap-2'
            onClick={() => setViewMode('audit')}
          >
            <FileText className='h-4 w-4' />
            Audit Log
          </Button>
        </div>
      </div>

      {viewMode === 'audit' ? (
        <UserAuditTab />
      ) : (
        <>
          {/* Header Actions */}
          <div className='flex justify-between items-center'>
            <Button
              onClick={() => setAddUserDialogOpen(true)}
              variant='default'
              size='sm'
              className='gap-2'
            >
              <UserIcon className='h-4 w-4' />
              Add New User
            </Button>
            <Button onClick={loadApprovals} variant='outline' size='sm'>
              <RefreshCcw className='h-4 w-4 mr-2' />
              Refresh Data
            </Button>
          </div>

          {/* Filters */}
          <div className='bg-card border rounded-lg p-4 space-y-4'>
            <h3 className='text-sm font-medium'>Filters</h3>
            <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
              <div className='space-y-2'>
                <label className='text-xs font-medium text-muted-foreground'>Search</label>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    placeholder='Search by email or name...'
                    className='pl-9'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <label className='text-xs font-medium text-muted-foreground'>Status</label>
                <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder='All Statuses' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Statuses</SelectItem>
                    <SelectItem value='pending'>Pending</SelectItem>
                    <SelectItem value='approved'>Approved</SelectItem>
                    <SelectItem value='rejected'>Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-xs font-medium text-muted-foreground'>Role</label>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder='All Roles' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Roles</SelectItem>
                    <SelectItem value='no_role'>No Role Assigned</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-xs font-medium text-muted-foreground'>Tenant</label>
                <Select
                  value={tenantFilter && tenantFilter.trim() !== '' ? tenantFilter : undefined}
                  onValueChange={setTenantFilter}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='All Tenants' />
                  </SelectTrigger>
                  <SelectContent className='z-[10000]'>
                    <SelectItem value='all'>All Tenants</SelectItem>
                    {allTenants?.map(tenant => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.tenant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className='text-sm text-muted-foreground'>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredApprovals.length)} of{' '}
            {filteredApprovals.length} users
          </div>

          {/* Table */}
          <div className='border rounded-lg bg-card overflow-hidden'>
            <div className='overflow-x-auto'>
              <Table className='min-w-[1200px]'>
                <TableHeader>
                  <TableRow>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Added On</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedApprovals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className='text-center py-12 text-muted-foreground'>
                        No user approvals found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedApprovals.map(approval => {
                      const userStatus = getUserStatus(approval);
                      const displayFirstName =
                        approval.first_name || approval.full_name?.split(' ')[0] || '-';
                      const displayLastName =
                        approval.last_name ||
                        approval.full_name?.split(' ').slice(1).join(' ') ||
                        '';
                      const displayFullName = displayLastName
                        ? `${displayFirstName} ${displayLastName}`.trim()
                        : displayFirstName;

                      return (
                        <TableRow key={approval.id}>
                          <TableCell>
                            <div className='flex items-center gap-2'>
                              <div className='h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center'>
                                <UserIcon className='h-4 w-4 text-primary' />
                              </div>
                              <div>
                                <div className='font-medium'>{displayFullName}</div>
                                {displayFirstName && displayLastName && (
                                  <div className='text-xs text-muted-foreground'>
                                    {displayFirstName} {displayLastName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-2'>
                              <Mail className='h-4 w-4 text-muted-foreground' />
                              <span>{approval.email}</span>
                            </div>
                          </TableCell>
                          <TableCell>{userStatus.badge}</TableCell>
                          <TableCell>{getEmailStatusBadge(approval)}</TableCell>
                          <TableCell>
                            {approval.status === 'approved' ? (
                              <Select
                                value={approval.user_role?.role_id || ''}
                                onValueChange={newRoleId => handleRoleChange(approval, newRoleId)}
                              >
                                <SelectTrigger className='w-[180px] h-8'>
                                  <SelectValue placeholder='No role assigned'>
                                    {approval.user_role ? (
                                      <div className='flex items-center gap-1'>
                                        <Shield className='h-3 w-3' />
                                        <span>{approval.user_role.role_display_name}</span>
                                      </div>
                                    ) : (
                                      <span className='text-muted-foreground'>No role</span>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map(role => (
                                    <SelectItem key={role.id} value={role.id}>
                                      <div className='flex flex-col'>
                                        <span className='font-medium'>{role.display_name}</span>
                                        {role.description && (
                                          <span className='text-xs text-muted-foreground'>
                                            {role.description}
                                          </span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : approval.user_role ? (
                              <Badge variant='outline' className='capitalize'>
                                <Shield className='h-3 w-3 mr-1' />
                                {approval.user_role.role_display_name}
                              </Badge>
                            ) : (
                              <span className='text-sm text-muted-foreground'>-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {approval.status === 'approved' ? (
                              allTenantsLoading ? (
                                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                  <div className='animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent' />
                                  Loading...
                                </div>
                              ) : allTenantsError ? (
                                <span className='text-sm text-destructive'>
                                  Error loading tenants
                                </span>
                              ) : allTenants && allTenants.length > 0 ? (
                                <Select
                                  value={approval.tenant_id || approval.user_tenants?.[0]?.id || ''}
                                  onValueChange={value => handleTenantSwitch(approval, value)}
                                >
                                  <SelectTrigger className='w-[220px] h-8'>
                                    <SelectValue placeholder='Select tenant'>
                                      {(() => {
                                        // Single source of truth so the trigger label can never drift
                                        // from the dropdown's value (pending users have no
                                        // user_tenants entries; fall back to allTenants by id).
                                        const displayTenant =
                                          approval.user_tenants?.[0] ??
                                          allTenants?.find(t => t.id === approval.tenant_id);
                                        return displayTenant ? (
                                          <div className='flex items-center gap-1'>
                                            <Building2 className='h-3 w-3' />
                                            <span>
                                              {displayTenant.name || displayTenant.tenant_name}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className='text-muted-foreground'>No tenant</span>
                                        );
                                      })()}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allTenants.map(tenant => (
                                      <SelectItem key={tenant.id} value={tenant.id}>
                                        <div className='flex items-center gap-2'>
                                          <Building2 className='h-3 w-3 text-muted-foreground' />
                                          <span>{tenant.name || tenant.tenant_name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className='text-sm text-muted-foreground'>No tenants</span>
                              )
                            ) : (
                              <span className='text-sm text-muted-foreground'>-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                              <Calendar className='h-4 w-4' />
                              {formatDate(approval.approved_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {approval.last_sign_in_at ? (
                              <div className='text-sm text-muted-foreground'>
                                {formatDate(approval.last_sign_in_at)}
                              </div>
                            ) : (
                              <span className='text-sm text-muted-foreground'>Never</span>
                            )}
                          </TableCell>
                          <TableCell className='text-right'>
                            <div className='flex items-center justify-end gap-2'>
                              {approval.status === 'pending' && (
                                <>
                                  <Button
                                    size='sm'
                                    variant='default'
                                    className='bg-green-600 hover:bg-green-700'
                                    onClick={() => openActionDialog(approval, 'approve')}
                                  >
                                    <CheckCircle className='h-4 w-4 mr-1' />
                                    Approve
                                  </Button>
                                  <Button
                                    size='sm'
                                    variant='outline'
                                    className='border-red-200 text-red-600 hover:bg-red-50'
                                    onClick={() => openActionDialog(approval, 'reject')}
                                  >
                                    <XCircle className='h-4 w-4 mr-1' />
                                    Reject
                                  </Button>
                                </>
                              )}
                              {(approval.status === 'approved' ||
                                approval.status === 'rejected') && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant='ghost'
                                      size='icon'
                                      className='h-8 w-8'
                                      title='More actions'
                                    >
                                      <MoreVertical className='h-4 w-4' />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align='end' className='w-56'>
                                    <DropdownMenuItem
                                      onClick={() => handleSendConfirmationLink(approval)}
                                      className='cursor-pointer'
                                    >
                                      <Mail className='h-4 w-4 mr-2' />
                                      Send Confirmation Link
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleSendResetPasswordLink(approval)}
                                      className='cursor-pointer'
                                    >
                                      <Key className='h-4 w-4 mr-2' />
                                      Send Reset Password Link
                                    </DropdownMenuItem>
                                    {approval.status === 'approved' && (
                                      <DropdownMenuItem
                                        onClick={() => handleRevokeAccess(approval)}
                                        className='cursor-pointer text-orange-600 dark:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400 focus:bg-orange-50 dark:focus:bg-orange-500/10'
                                      >
                                        <Ban className='h-4 w-4 mr-2' />
                                        Revoke Access
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => openRemoveDialog(approval)}
                                      className='cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-500/10'
                                    >
                                      <Trash2 className='h-4 w-4 mr-2' />
                                      Remove User
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Controls */}
          {viewMode === 'users' && filteredApprovals.length > 0 && (
            <div className='flex items-center justify-between mt-4'>
              <div className='flex items-center gap-2'>
                <span className='text-sm text-muted-foreground'>Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={value => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className='w-16 h-8 text-sm'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='5'>5</SelectItem>
                    <SelectItem value='10'>10</SelectItem>
                    <SelectItem value='25'>25</SelectItem>
                    <SelectItem value='50'>50</SelectItem>
                    <SelectItem value='100'>100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className='hidden sm:inline-flex'
                >
                  First
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <div className='flex items-center gap-1'>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setCurrentPage(pageNum)}
                        className='w-8 h-8 p-0'
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className='hidden sm:inline-flex'
                >
                  Last
                </Button>
              </div>
            </div>
          )}

          {/* Last Refresh Indicator - Show most recent approval time */}
          <div className='flex justify-center pt-4'>
            {approvals.length > 0 ? (
              <div className='text-sm text-muted-foreground italic'>
                {(() => {
                  const timestamps = approvals
                    .map(a => {
                      const requested = a.requested_at ? new Date(a.requested_at).getTime() : 0;
                      const approved = a.approved_at ? new Date(a.approved_at).getTime() : 0;
                      return Math.max(requested, approved);
                    })
                    .filter(ts => ts > 0); // Filter out invalid dates

                  if (timestamps.length === 0) {
                    return 'No valid approval dates';
                  }

                  const mostRecent = Math.max(...timestamps);
                  return `Most recent approval: ${new Date(mostRecent).toLocaleString()}`;
                })()}
              </div>
            ) : (
              <div className='text-sm text-muted-foreground italic'>No approvals loaded</div>
            )}
          </div>

          {/* Action Dialog */}
          <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {actionType === 'approve' ? (
                    <span className='flex items-center gap-2 text-green-600'>
                      <CheckCircle className='h-5 w-5' />
                      Approve User Access
                    </span>
                  ) : (
                    <span className='flex items-center gap-2 text-red-600'>
                      <XCircle className='h-5 w-5' />
                      Reject User Access
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {actionType === 'approve'
                    ? `Grant ${selectedApproval?.email} access to the application.`
                    : `Deny ${selectedApproval?.email} access to the application.`}
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-4 py-4'>
                {/* User Info */}
                <div className='bg-muted/50 rounded-lg p-4 space-y-2'>
                  <div className='flex items-center gap-2'>
                    <Mail className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm font-medium'>{selectedApproval?.email}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <UserIcon className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>
                      {selectedApproval?.full_name || 'No name provided'}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Shield className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm capitalize'>{selectedApproval?.auth_provider}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Calendar className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>
                      Requested: {formatDate(selectedApproval?.requested_at || null)}
                    </span>
                  </div>
                </div>

                {/* Role Selection - only for approve action */}
                {actionType === 'approve' && (
                  <div className='space-y-2'>
                    <label className='text-sm font-medium text-red-600'>Assign Role *</label>
                    <Select
                      value={
                        selectedRoleId && selectedRoleId.trim() !== '' ? selectedRoleId : undefined
                      }
                      onValueChange={value => setSelectedRoleId(value)}
                      required
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue
                          placeholder={
                            roles.length === 0
                              ? 'Loading roles...'
                              : 'Select a role for this user...'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className='z-[10000]'>
                        {roles.length === 0 ? (
                          <SelectItem value='loading' disabled>
                            Loading roles...
                          </SelectItem>
                        ) : (
                          roles.map(role => (
                            <SelectItem key={role.id} value={role.id}>
                              <div className='flex flex-col'>
                                <span className='font-medium'>{role.display_name}</span>
                                {role.description && (
                                  <span className='text-xs text-muted-foreground'>
                                    {role.description}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className='text-xs text-muted-foreground'>
                      This determines what permissions and access the user will have in the system.
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    {actionType === 'reject' ? 'Reason (Required)' : 'Notes (Optional)'}
                  </label>
                  <Textarea
                    placeholder={
                      actionType === 'reject'
                        ? 'Explain why access is being denied...'
                        : 'Add any additional notes...'
                    }
                    value={actionNotes}
                    onChange={e => setActionNotes(e.target.value)}
                    rows={3}
                    required={actionType === 'reject'}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant='outline' onClick={() => setActionDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant={actionType === 'approve' ? 'default' : 'destructive'}
                  onClick={handleAction}
                  disabled={
                    (actionType === 'approve' && !selectedRoleId) ||
                    (actionType === 'reject' && !actionNotes.trim())
                  }
                  className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {actionType === 'approve' ? (
                    <>
                      <CheckCircle className='h-4 w-4 mr-2' />
                      Approve User
                    </>
                  ) : (
                    <>
                      <XCircle className='h-4 w-4 mr-2' />
                      Reject User
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Remove User Dialog */}
          <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  <span className='flex items-center gap-2 text-red-600'>
                    <Trash2 className='h-5 w-5' />
                    Remove User
                  </span>
                </DialogTitle>
                <DialogDescription>
                  This action will permanently delete the user from the system. This cannot be
                  undone.
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-4 py-4'>
                {/* User Info */}
                <div className='bg-muted/50 rounded-lg p-4 space-y-2'>
                  <div className='flex items-center gap-2'>
                    <Mail className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm font-medium'>{userToRemove?.email}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <UserIcon className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>{userToRemove?.full_name || 'No name provided'}</span>
                  </div>
                  {userToRemove?.user_role && (
                    <div className='flex items-center gap-2'>
                      <Shield className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm'>{userToRemove.user_role.role_display_name}</span>
                    </div>
                  )}
                </div>

                {/* Warning */}
                <div className='bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg p-4'>
                  <p className='text-sm text-red-800 dark:text-red-200'>
                    <strong>Warning:</strong> This will permanently delete the user from the system.
                    This action cannot be undone and will:
                  </p>
                  <ul className='text-sm text-red-800 dark:text-red-200 mt-2 ml-4 list-disc'>
                    <li>Delete the user from Supabase Auth</li>
                    <li>Remove all role assignments</li>
                    <li>Remove all tenant associations</li>
                    <li>Delete approval records</li>
                    <li>Delete user profile data</li>
                  </ul>
                  <p className='text-sm text-red-800 dark:text-red-200 mt-2'>
                    This action will be logged in the audit trail.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant='outline' onClick={() => setRemoveDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant='destructive' onClick={handleRemoveUser}>
                  <Trash2 className='h-4 w-4 mr-2' />
                  Remove User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add New User Dialog */}
          <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
            <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle>
                  <span className='flex items-center gap-2'>
                    <UserIcon className='h-5 w-5' />
                    Add New User
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Create a new user account using Supabase's invite flow. An invitation email will
                  be sent to the user with a link to set their password. The user will be approved
                  immediately and can sign in once they set their password.
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-4 py-4'>
                {/* Email */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    Email <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    type='email'
                    placeholder='user@example.com'
                    value={newUserEmail}
                    onChange={e => setNewUserEmail(e.target.value)}
                    required
                  />
                  <p className='text-xs text-muted-foreground'>
                    User will receive an email invitation to set their password at portal.vehix.ai
                  </p>
                </div>

                {/* First Name */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    First Name <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    type='text'
                    placeholder='John'
                    value={newUserFirstName}
                    onChange={e => setNewUserFirstName(e.target.value)}
                    required
                  />
                </div>

                {/* Last Name */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    Last Name <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    type='text'
                    placeholder='Doe'
                    value={newUserLastName}
                    onChange={e => setNewUserLastName(e.target.value)}
                    required
                  />
                </div>

                {/* Role Selection */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    Role <span className='text-red-600'>*</span>
                  </label>
                  <Select
                    value={newUserRoleId && newUserRoleId.trim() !== '' ? newUserRoleId : undefined}
                    onValueChange={value => setNewUserRoleId(value)}
                    required
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue
                        placeholder={
                          roles.length === 0 ? 'Loading roles...' : 'Select a role for this user...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className='z-[10000]'>
                      {roles.length === 0 ? (
                        <SelectItem value='loading' disabled>
                          Loading roles...
                        </SelectItem>
                      ) : (
                        roles.map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className='flex flex-col'>
                              <span className='font-medium'>{role.display_name}</span>
                              {role.description && (
                                <span className='text-xs text-muted-foreground'>
                                  {role.description}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    This determines what permissions and access the user will have in the system.
                  </p>
                </div>

                {/* Tenant Selection (single tenant only) */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>
                    Tenant <span className='text-red-600'>*</span>
                  </label>
                  {allTenantsLoading ? (
                    <div className='flex items-center justify-center py-4'>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
                      <span className='ml-2 text-sm'>Loading tenants...</span>
                    </div>
                  ) : allTenantsError ? (
                    <div className='text-sm text-destructive p-2 border border-destructive rounded'>
                      <AlertCircle className='h-4 w-4 inline mr-2' />
                      Error loading tenants. Please refresh the page.
                    </div>
                  ) : allTenants && allTenants.length > 0 ? (
                    <Select value={newUserTenantId} onValueChange={setNewUserTenantId}>
                      <SelectTrigger>
                        <SelectValue placeholder='Select the tenant for this user' />
                      </SelectTrigger>
                      <SelectContent>
                        {allTenants.map(tenant => (
                          <SelectItem key={tenant.id} value={tenant.id}>
                            <div className='flex items-center gap-2'>
                              <Building2 className='h-3 w-3 text-muted-foreground' />
                              <span>{tenant.name || tenant.tenant_name}</span>
                              {tenant.tenant_slug && (
                                <Badge variant='outline' className='text-xs'>
                                  {tenant.tenant_slug}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className='text-sm text-muted-foreground p-2 border rounded'>
                      No tenants available. Please create a tenant first.
                    </div>
                  )}
                  <p className='text-xs text-muted-foreground'>
                    Select the tenant for this user. They will be assigned to this tenant only.
                  </p>
                </div>

                {/* Notes */}
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>Notes (Optional)</label>
                  <Textarea
                    placeholder='Add any additional notes about this user...'
                    value={newUserNotes}
                    onChange={e => setNewUserNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant='outline'
                  onClick={() => {
                    setAddUserDialogOpen(false);
                    setNewUserEmail('');
                    setNewUserFirstName('');
                    setNewUserLastName('');
                    setNewUserRoleId('');
                    setNewUserTenantId('');
                    setNewUserNotes('');
                  }}
                  disabled={creatingUser}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={
                    creatingUser ||
                    !newUserEmail ||
                    !newUserFirstName ||
                    !newUserLastName ||
                    !newUserRoleId ||
                    !newUserTenantId
                  }
                >
                  {creatingUser ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className='h-4 w-4 mr-2' />
                      Create User
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
